import $ = require("jquery");
import _ = require("underscore");
import arcgis = require("arcgis/services/featureserver/layer");
import Portal = require("portal-gis");
import Defaults = require("../../models/defaults");

const styleMap = {
  esriSMSCircle: "circle",
  esriSMSDiamond: "diamond",
  esriSMSX: "x",
  esriSMSCross: "cross",
  esriSLSSolid: "solid",
  esriSFSSolid: "solid",
  esriSLSDot: "dot",
  esriSLSDash: "dash",
  esriSLSDashDot: "dashdot",
  esriSLSDashDotDot: "dashdotdot",
};

const typeMap = {
  esriSMS: "sms", // simple marker symbol
  esriSLS: "sls", // simple line symbol
  esriSFS: "sfs", // simple fill symbol
  esriPMS: "pms", // picture marker symbol
  esriPFS: "pfs", // picture fill symbol
  esriTS: "txt", // text symbol
};

function as(v) {
  return "" + v + "px";
}

// convert from ags style to an internal format
class ConvertToNative {
  static about = `convert from ags style to an internal format`;

  // convert drawing info into a symbology rule
  public asSymbology(
    drawingInfo: arcgis.DrawingInfo,
    args: {
      url: string;
    }
  ) {
    let symbologies = <Portal.MapSymbologyElements>{
      IconHeight: 0,
      IconWidth: 0,
      Symbols: [],
    };

    let symbology = {
      id: drawingInfo.renderer.label || args.url,
      Label: drawingInfo.renderer.label,
      Icons: [],
    };
    symbologies.Symbols.push(symbology);

    console.log(`processing ${drawingInfo.renderer.type} renderer`);

    switch (drawingInfo.renderer.type) {
      case "simple": {
        // {"type":"esriSFS","style":"esriSFSSolid","color":[246,197,103,255],"outline":{"type":"esriSLS","style":"esriSLSSolid","color":[0,0,0,255],"width":0.4}}
        let icon = this.asIcon(drawingInfo.renderer.symbol, {
          url: args.url,
          id: drawingInfo.renderer.label,
          label: drawingInfo.renderer.label,
        });
        symbology.Icons.push(icon);
        break;
      }

      case "uniqueValue": {
        symbology.Label = drawingInfo.renderer.defaultLabel || symbology.Label;

        if (drawingInfo.renderer.defaultSymbol) {
          console.log("processing defaultSymbol");
          // first icon is the default rule
          let icon = this.asIcon(drawingInfo.renderer.defaultSymbol, {
            id: "*",
            url: args.url,
            label: drawingInfo.renderer.defaultLabel,
          });
          symbology.Icons.push(icon);
        }

        if (drawingInfo.renderer.uniqueValueInfos) {
          console.log("processing uniqueValueInfos");
          // each value is a new icon
          let icons = drawingInfo.renderer.uniqueValueInfos.map((info) => {
            let icon = this.asIcon(info.symbol, {
              url: args.url,
              id: `${info.label}-${info.value}`,
              label: info.label,
            });

            icon.Filters = {
              Filters: [
                {
                  id: drawingInfo.renderer.field1,
                  Value: "=" + info.value,
                },
              ],
            };

            return icon;
          });

          icons.forEach((icon) => symbology.Icons.push(icon));
        }
        break;
      }

      case "classBreaks": {
        let renderer = <arcgis.ClassBreakRenderer>drawingInfo.renderer;
        if (renderer.classBreakInfos) {
          console.log("processing classBreakInfos");
          if (renderer.visualVariables) {
            renderer.visualVariables.forEach((vars) => {
              switch (vars.type) {
                /**
                 * This renderer adjusts the size of the symbol to between [minSize..maxSize]
                 * based on the range of values [minDataValue, maxDataValue]
                 */
                case "sizeInfo": {
                  let steps = _.range(
                    renderer.authoringInfo.visualVariables[0].minSliderValue,
                    renderer.authoringInfo.visualVariables[0].maxSliderValue
                  );
                  let dx = (vars.maxSize - vars.minSize) / steps.length;
                  let dataValue = (vars.maxDataValue - vars.minDataValue) / steps.length;

                  renderer.classBreakInfos.forEach((classBreakInfo) => {
                    let icons = steps.map((step) => {
                      let symbol = _.clone(classBreakInfo.symbol);
                      symbol.size = vars.minSize + dx * (dataValue - vars.minDataValue);
                      let icon = this.asIcon(symbol, {
                        url: "",
                        id: "",
                        label: "",
                      });
                      icon.Filters.Filters.push({ id: renderer.field, Value: "<" + (dataValue + dx) });
                      return icon;
                    });
                    icons.forEach((icon) => symbology.Icons.push(icon));
                  });
                  break;
                }
                default:
                  break;
              }
            });
          }
        }
        break;
      }

      default: {
        console.log("unsupported renderer type: ", drawingInfo.renderer.type);
        break;
      }
    }

    symbologies.Symbols.forEach((s) => Defaults.asSymbolTemplate(s));

    return symbologies;
  }

  private asOutline(symbol: arcgis.Outline) {
    symbol.style = styleMap[symbol.style] || symbol.style;
    symbol.type = typeMap[symbol.type] || symbol.type;
  }

  // convert a ags symbol into a symbology rule
  public asIcon(
    symbol: arcgis.Symbol,
    args: {
      url: string;
      id: string;
      label: string;
    }
  ) {
    let icon = <Portal.MapIconConfigElement>{
      id: args.id,
      Width: symbol.size,
      Height: symbol.size,
      Label: args.label,
    };

    switch (symbol.type) {
      case "esriSMS":
        break;

      case "esriPMS":
      case "esriPFS":
        // this is no good...should use requirejs.toUrl here
        if (symbol.url && 0 > symbol.url.indexOf("//")) {
          symbol.url = `${args.url}/images/${symbol.url}`;
        }
        break;

      case "esriSLS":
        break;

      case "esriSFS":
        switch (symbol.style) {
          case "esriSFSSolid":
          case "esriSFSForwardDiagonal":
            break;
        }
        break;

      case "esriTS": {
        let textStyle = <Rhythm.Symbology.TextSymbol>(<any>symbol);
        if (symbol.font) {
          textStyle.font =
            symbol.font.style + " " + symbol.font.weight + " " + symbol.font.size + " px " + symbol.font.family;
        }

        if (symbol.verticalAlignment) {
          textStyle.textBaseline = symbol.verticalAlignment;
          delete symbol.verticalAlignment;
        }

        if (symbol.horizontalAlignment) {
          textStyle.textAlign = symbol.horizontalAlignment;
          delete symbol.horizontalAlignment;
        }
        break;
      }

      default: {
        console.warn("symbol type not found: ", symbol.type);
      }
    }

    symbol.outline && this.asOutline(symbol.outline);

    symbol.style = styleMap[symbol.style] || symbol.style;
    symbol.type = typeMap[symbol.type] || symbol.type;

    icon.type = "style";
    icon.style = JSON.stringify(symbol);

    return Defaults.asIconTemplate(icon);
  }
}

export = ConvertToNative;
